/**
 * A small neutral token for a categorical value: a pathway, an anchor species, a
 * mechanism class.
 *
 * Deliberately **not** colour-coded per value. The obvious design is a hash of the
 * pathway name into a palette so the same pathway is always the same hue, and it is
 * wrong here for three reasons. `pathway` is unbounded free text written by the
 * model, so the palette would be assigned by a hash nobody can predict and reused
 * across unrelated names. Of the seven themed families, `red`, `amber` and `yellow`
 * already mean *status* on this page, so a pathway landing on red would read as a
 * fault. And a per-value hex, which is how such a palette is normally written, is
 * outside the theme system entirely: it cannot reverse for the light theme and
 * `tests/test_ui_theme.py` would never see it.
 *
 * What a reader actually wants from pathway — all the fatty-acid targets together —
 * is grouping, so the tables offer group-by-pathway and the chip stays quiet.
 *
 * `mono` for accessions and ids, which are compared character by character.
 */
import type { HTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** `mono` for an accession, a PDB id or a run id. */
  font?: 'sans' | 'mono'
  /** Draw attention without claiming a status: for the value being grouped on. */
  emphasis?: boolean
}

export function Chip({
  className,
  font = 'sans',
  emphasis = false,
  ...props
}: ChipProps) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 truncate rounded-md border px-1.5 py-0.5 text-2xs',
        font === 'mono' ? 'font-mono' : 'font-sans',
        emphasis
          ? 'border-slate-600 bg-slate-800 text-slate-200'
          : 'border-slate-700 bg-slate-800/60 text-slate-400',
        className,
      )}
      {...props}
    />
  )
}
